import { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Chip,
    Grid,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TableHead,
    Link,
    Tooltip,
    Dialog,
    DialogContent,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import AndroidOutlinedIcon from '@mui/icons-material/AndroidOutlined';
import AppleIcon from '@mui/icons-material/Apple';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import LanguageIcon from '@mui/icons-material/Language';
import AllOutIcon from '@mui/icons-material/AllOut';
import TabletIcon from '@mui/icons-material/Tablet';
import DescriptionIcon from '@mui/icons-material/Description';
import UserOrderShippingPriceInfos from './UserOrderShippingPriceInfos';
import { RotateLeft } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import DiscountIcon from '@mui/icons-material/Discount';
import Alert from '@mui/material/Alert';
import CopyToClipboardButton from '@/atomic/atoms/SCUICopyToClipboardButton/SCUICopyToClipboardButton';
import { UOPT, uopdCls } from '../userOrderProductDetails.classes';

const formatCampaignDiscountAmount = (campItem: any): string => {
    const amount = campItem.discountAmountPerUnit || '';
    const currency = campItem.currency || '';

    const numeric = Number(amount);
    if (!Number.isFinite(numeric)) {
        return '-';
    }
    return `${numeric.toFixed(2)} ${currency}`;
};

function UserOrderProductTable({ shipment, isExpanded, saleCh }: any) {
    const { t } = useTranslation();
    const [openModal, setOpenModal] = useState(false);
    const [selectedImage, setSelectedImage] = useState('');
    const returnDetails: any = useSelector((state: any) => state?.returnDetails);
    const handleOpenModal = (imageUrl: any) => {
        setSelectedImage(imageUrl);
        setOpenModal(true);
    };
    const handleCloseModal = () => {
        setOpenModal(false);
    };
    const renderCell = (saleCh: any) => {
        switch (saleCh) {
            case 'Android':
                return <AndroidOutlinedIcon />;
            case 'IOS':
                return <AppleIcon />;
            case 'MobileAssistant':
                return <SmartphoneIcon />;
            case 'WindowsPhone':
                return <PhoneAndroidIcon />;
            case 'Web':
                return <LanguageIcon />;
            case 'WebAPI':
                return <AllOutIcon />;
            case 'WKioskTablet':
                return <TabletIcon />;
            default:
                return null;
        }
    };
    const matchesReturnDetail = (row: any, detail: any) => {
        const productIds =
            Array.isArray(row.groupedProductIds) && row.groupedProductIds.length > 0
                ? row.groupedProductIds
                : [row.productId];
        const orderItemIds =
            Array.isArray(row.groupedOrderItemIds) && row.groupedOrderItemIds.length > 0
                ? row.groupedOrderItemIds
                : [row.orderItemId];

        const productMatch = productIds.some((id: any) => id == detail.productId);
        const orderItemMatch = orderItemIds.some((id: any) => id == detail.orderItemId);
        const shippingMatch = detail.shippingListId == shipment?.shippingListId;

        return productMatch && orderItemMatch && shippingMatch;
    };

    const getGroupedReturnDetailsForRow = (row: any) => {
        const matchingDetails = (returnDetails?.data || []).filter((detail: any) => matchesReturnDetail(row, detail));

        const groupedByStatus = matchingDetails.reduce((acc: Record<string, any>, detail: any) => {
            const groupKey = `${detail.statusId ?? ''}_${detail.message ?? ''}`;
            if (!acc[groupKey]) {
                acc[groupKey] = {
                    statusId: detail.statusId,
                    message: detail.message,
                    totalQuantity: 0,
                };
            }
            acc[groupKey].totalQuantity += Number(detail.totalQuantity) || 0;
            return acc;
        }, {});

        return Object.values(groupedByStatus);
    };

    const renderReturnRequestBanner = (detail: any, detailIndex: number) => (
        <div
            key={detailIndex}
            className={UOPT.returnMessageWrap}
            style={{
                backgroundColor: '#ffcc80',
                color: 'black',
                textAlign: 'left',
                fontWeight: 'lighter',
                fontSize: '12px',
                borderRadius: '4px',
                margin: '10px',
                padding: '10px',
                maxWidth: '300px',
                display: 'flex',
                boxShadow: '0px -4px 6px rgba(0, 0, 0, 0.1), 0px 4px 6px rgba(0, 0, 0, 0.1)',
            }}
        >
            <RotateLeft style={{ marginRight: '10px', color: 'white' }} />
            <p style={{ fontWeight: 'bolder' }}>
                {detail.totalQuantity} {detail.message ? t(`orderDetail.${detail.message}`) : ''}
            </p>
        </div>
    );

    return (
        <>
            <Dialog className={UOPT.dialog} open={openModal} onClose={handleCloseModal}>
                <DialogContent className={UOPT.dialogContent}>
                    <img
                        className={UOPT.dialogImage}
                        src={selectedImage}
                        style={{ width: '400px', height: 'auto' }}
                        alt=""
                    />
                </DialogContent>
            </Dialog>
            {shipment ? (
                <Card className={UOPT.card} style={{ height: 'auto' }}>
                    <CardContent className={UOPT.cardContent}>
                        <TableContainer className={UOPT.tableContainer} component={Paper}>
                            <Table className={UOPT.table} sx={{ minWidth: 700 }}>
                                <TableHead>
                                    <TableRow className={UOPT.tableHeadRow}>
                                        <TableCell className={UOPT.photoCell}>{t('orderDetail.photo')}</TableCell>
                                        <TableCell className={UOPT.productCell}>{t('orderDetail.product')}</TableCell>
                                        <TableCell
                                            align="center"
                                            className={UOPT.optionIdCell}
                                            style={{ maxWidth: 100 }}
                                        >
                                            {t('orderDetail.growStore')} / {t('orderDetail.crossBorderProduct')}
                                        </TableCell>
                                        <TableCell align="center">{t('orderDetail.saleCh')}</TableCell>
                                        <TableCell align="center">{t('orderDetail.quantity')}</TableCell>
                                        <TableCell align="center">{t('orderDetail.price')}</TableCell>
                                        <TableCell align="center">{t('orderDetail.salePrice')}</TableCell>
                                        <TableCell align="center"></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {shipment.shippingListItemForBackofficeModels.map((row: any, index: number) => (
                                        <>
                                            <TableRow
                                                key={`${shipment?.shippingListId ?? 's'}-${row.barcode ?? ''}-${row.orderItemId ?? ''}-${index}`}
                                                className={uopdCls(
                                                    `slide-animation ${isExpanded ? 'slide-in' : ''}`,
                                                    UOPT.tableBodyRow
                                                )}
                                                style={{ animationDelay: `${index * 1}s` }}
                                            >
                                                <TableCell
                                                    className={UOPT.photoCell}
                                                    align="left"
                                                    sx={{ borderBottom: 'none', minWidth: 200, verticalAlign: 'top' }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'stretch',
                                                            gap: 1.5,
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                position: 'relative',
                                                                flexShrink: 0,
                                                                width: 120,
                                                                minHeight: 120,
                                                                borderRadius: '10px',
                                                                overflow: 'hidden',
                                                                border: '1px solid #E5E7EB',
                                                                backgroundColor: '#F9FAFB',
                                                            }}
                                                        >
                                                            <Box
                                                                component="img"
                                                                src={row.imageUrl}
                                                                alt={row.marka}
                                                                sx={{
                                                                    display: 'block',
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'contain',
                                                                }}
                                                            />
                                                            <Box
                                                                className={UOPT.openImageIconWrap}
                                                                sx={{
                                                                    position: 'absolute',
                                                                    top: 4,
                                                                    right: 4,
                                                                    cursor: 'pointer',
                                                                    color: '#5b99f3',
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                                    borderRadius: '6px',
                                                                    lineHeight: 0,
                                                                    p: 0.25,
                                                                }}
                                                                onClick={() => handleOpenModal(row.imageUrl)}
                                                            >
                                                                <ZoomInIcon
                                                                    className={UOPT.openImageIcon}
                                                                    sx={{ width: 20, height: 20 }}
                                                                />
                                                            </Box>
                                                        </Box>
                                                        <Box
                                                            sx={{
                                                                flex: 1,
                                                                minWidth: 0,
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                gap: 1,
                                                            }}
                                                        >
                                                            {getGroupedReturnDetailsForRow(row).map(
                                                                (detail: any, detailIndex: number) =>
                                                                    renderReturnRequestBanner(detail, detailIndex)
                                                            )}
                                                            {row.campaigns && row.campaigns.length > 0 && (
                                                                <Box
                                                                    className={UOPT.campaignDiscountWrap}
                                                                    sx={{
                                                                        maxWidth: 320,
                                                                        p: 0.5,
                                                                        borderRadius: '12px',
                                                                        border: '1px solid #FED7AA',
                                                                        backgroundColor: '#FFF7ED',
                                                                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
                                                                    }}
                                                                >
                                                                    <Stack spacing={0.5} className={UOPT.campaignList}>
                                                                        {row.campaigns.map(
                                                                            (campItem: any, campIndex: number) =>
                                                                                campItem ? (
                                                                                    <Box
                                                                                        key={
                                                                                            campItem.discountSourceId ??
                                                                                            campIndex
                                                                                        }
                                                                                        className={`slide-animation ${
                                                                                            isExpanded ? 'slide-in' : ''
                                                                                        } ${UOPT.campaignItem}`}
                                                                                        data-discount-source-id={
                                                                                            campItem.discountSourceId
                                                                                        }
                                                                                        sx={{
                                                                                            display: 'flex',
                                                                                            alignItems: 'center',
                                                                                            gap: 0.5,
                                                                                            p: 0.5,
                                                                                            borderRadius: '10px',
                                                                                            border: '1px solid #FFEDD5',
                                                                                            backgroundColor: '#FFFFFF',
                                                                                            transition:
                                                                                                'box-shadow 0.15s ease, border-color 0.15s ease',
                                                                                            animationDelay: `${campIndex * 0.15}s`,
                                                                                            '&:hover': {
                                                                                                borderColor: '#FDBA74',
                                                                                                boxShadow:
                                                                                                    '0 4px 12px rgba(234, 88, 12, 0.12)',
                                                                                            },
                                                                                        }}
                                                                                    >
                                                                                        <DiscountIcon
                                                                                            sx={{
                                                                                                color: '#EA580C',
                                                                                                fontSize: 16,
                                                                                                mt: 0.25,
                                                                                                mr: 0.5,
                                                                                                flexShrink: 0,
                                                                                            }}
                                                                                        />
                                                                                        <Box
                                                                                            sx={{
                                                                                                flex: 1,
                                                                                                minWidth: 0,
                                                                                            }}
                                                                                        >
                                                                                            <Typography
                                                                                                className={
                                                                                                    UOPT.campaignItemId
                                                                                                }
                                                                                                variant="caption"
                                                                                                sx={{
                                                                                                    display: 'block',
                                                                                                    color: '#78716C',
                                                                                                    mb: 0.1,
                                                                                                }}
                                                                                            >
                                                                                                {t(
                                                                                                    'orderDetail.campaignId'
                                                                                                )}
                                                                                                :{' '}
                                                                                                {
                                                                                                    campItem.discountSourceId
                                                                                                }
                                                                                            </Typography>
                                                                                            <Typography
                                                                                                className={
                                                                                                    UOPT.campaignItemName
                                                                                                }
                                                                                                variant="body2"
                                                                                                sx={{
                                                                                                    fontWeight: 600,
                                                                                                    color: '#374151',
                                                                                                    lineHeight: 1.4,
                                                                                                    wordBreak:
                                                                                                        'break-word',
                                                                                                }}
                                                                                            >
                                                                                                {campItem.description}
                                                                                            </Typography>
                                                                                        </Box>
                                                                                        <Chip
                                                                                            className={
                                                                                                UOPT.campaignItemAmount
                                                                                            }
                                                                                            label={formatCampaignDiscountAmount(
                                                                                                campItem
                                                                                            )}
                                                                                            size="small"
                                                                                            sx={{
                                                                                                height: 28,
                                                                                                flexShrink: 0,
                                                                                                fontWeight: 700,
                                                                                                fontSize: '0.8125rem',
                                                                                                color: '#C2410C',
                                                                                                backgroundColor:
                                                                                                    '#FFEDD5',
                                                                                                border: '1px solid #FDBA74',
                                                                                            }}
                                                                                        />
                                                                                    </Box>
                                                                                ) : null
                                                                        )}
                                                                    </Stack>
                                                                </Box>
                                                            )}
                                                            {row.returnMessage && (
                                                                <div
                                                                    className={UOPT.returnErrorWrap}
                                                                    style={{
                                                                        maxWidth: '300px',
                                                                        overflowY: 'auto',
                                                                        maxHeight: '100px',
                                                                    }}
                                                                >
                                                                    <Stack spacing={2}>
                                                                        <Alert severity="error">
                                                                            <p style={{ fontSize: '12px' }}>
                                                                                {row.returnMessage}
                                                                            </p>
                                                                        </Alert>
                                                                    </Stack>
                                                                </div>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell
                                                    className={UOPT.productCell}
                                                    component="th"
                                                    scope="row"
                                                    style={{ borderBottom: 'none', maxWidth: 180 }}
                                                >
                                                    <div>
                                                        <Grid>
                                                            <p style={{ color: 'red' }}>{row.brandName}</p>
                                                        </Grid>
                                                        <Grid>{row.productDescriptionOnWebSite}</Grid>
                                                        <Tooltip title={t('orderDetail.barcode')} placement="left">
                                                            <Grid>
                                                                <strong>{row.barcode}</strong>
                                                                <CopyToClipboardButton
                                                                    className={UOPT.barcodeCopy}
                                                                    textToCopy={row.barcode || ''}
                                                                    size="small"
                                                                />
                                                            </Grid>
                                                        </Tooltip>
                                                        <Tooltip
                                                            title={t('orderDetail.specialAndColorCode')}
                                                            placement="left"
                                                        >
                                                            <Grid>
                                                                <Link
                                                                    className={UOPT.productOptionLink}
                                                                    target="_blank"
                                                                    href={'/productDetail/' + row.optionId}
                                                                >
                                                                    {row.ozelKod1} - {row.renkKod}
                                                                </Link>
                                                                <CopyToClipboardButton
                                                                    className={UOPT.ozellikKodCopy}
                                                                    textToCopy={
                                                                        row.ozelKod1 + ' - ' + row.renkKod || ''
                                                                    }
                                                                    size="small"
                                                                />
                                                            </Grid>
                                                        </Tooltip>
                                                        <Tooltip title={'OptionId'} placement="left">
                                                            <Grid>
                                                                {row.optionId}
                                                                <CopyToClipboardButton
                                                                    className={UOPT.optionIdCopy}
                                                                    textToCopy={row.optionId || ''}
                                                                    size="small"
                                                                />
                                                            </Grid>
                                                        </Tooltip>
                                                        <Tooltip
                                                            title={t('general.color') + ' - ' + t('orderDetail.size')}
                                                            placement="left"
                                                        >
                                                            <Grid>
                                                                {row.optionTitleKey + ' - ' + row.size}{' '}
                                                                <CopyToClipboardButton
                                                                    className={UOPT.colorSizeCopy}
                                                                    textToCopy={
                                                                        row.optionTitleKey + ' - ' + row.size || ''
                                                                    }
                                                                    size="small"
                                                                />
                                                            </Grid>
                                                        </Tooltip>
                                                    </div>
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    style={{ borderBottom: 'none', fontSize: '0.75rem', maxWidth: 100 }}
                                                >
                                                    {row.isGrowProduct ? row.warehouseName : '-'} /{' '}
                                                    {shipment.isCrossDockable ? t('general.yes') : t('general.no')}
                                                </TableCell>
                                                <TableCell align="center" style={{ borderBottom: 'none' }}>
                                                    {renderCell(saleCh)} {saleCh}
                                                </TableCell>
                                                <TableCell align="center" style={{ borderBottom: 'none' }}>
                                                    {row.quantity}
                                                </TableCell>
                                                <TableCell align="center" style={{ borderBottom: 'none' }}>
                                                    {row.unitPrice}
                                                </TableCell>
                                                <TableCell align="center" style={{ borderBottom: 'none' }}>
                                                    {<strong>{row.salePrice}</strong>}
                                                </TableCell>
                                                {row.originalOrderId && (
                                                    <TableCell align="center" style={{ borderBottom: 'none' }}>
                                                        <Link
                                                            href={`/orderdetail?orderId=${row.originalOrderId}`}
                                                            target="_blank"
                                                        >
                                                            <DescriptionIcon
                                                                style={{
                                                                    marginRight: '0.5rem',
                                                                    verticalAlign: 'middle',
                                                                }}
                                                            />
                                                            {t('orderDetail.mainOrder')}
                                                        </Link>
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        </>
                                    ))}
                                </TableBody>
                            </Table>
                            <UserOrderShippingPriceInfos shipment={shipment} />
                        </TableContainer>
                    </CardContent>
                </Card>
            ) : (
                <div>{t('general.noData')}</div>
            )}
        </>
    );
}

export default UserOrderProductTable;
